import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Phone, PhoneOff, Mic, MicOff, Video, VideoOff, 
  ArrowLeft, User, Volume2, VolumeX
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const WS_API = `${WS_URL}/api`;

// ICE servers for WebRTC (using free STUN servers)
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ]
};

const VideoCallPage = () => {
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Call state
  const [callState, setCallState] = useState('connecting'); // connecting, ringing, active, ended
  const [callData, setCallData] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  
  // Media state
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(true); // Start with video off for privacy
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const wsRef = useRef(null);
  const callTimerRef = useRef(null);
  
  const isInitiator = searchParams.get('initiator') === 'true';
  const chatId = searchParams.get('chatId');

  // Initialize media stream
  const initializeMedia = useCallback(async (videoEnabled = false) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: videoEnabled
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Could not access microphone. Please check permissions.');
      return null;
    }
  }, []);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
          callId: callId
        }));
      }
    };
    
    pc.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
    
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallState('active');
        // Start call timer
        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCall();
      }
    };
    
    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    peerConnectionRef.current = pc;
    return pc;
  }, [callId]);

  // Handle incoming signaling messages
  const handleSignalingMessage = useCallback(async (message) => {
    const data = JSON.parse(message.data);
    
    if (data.callId !== callId) return;
    
    switch (data.type) {
      case 'offer':
        if (peerConnectionRef.current) {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await peerConnectionRef.current.createAnswer();
          await peerConnectionRef.current.setLocalDescription(answer);
          wsRef.current.send(JSON.stringify({
            type: 'answer',
            answer: answer,
            callId: callId
          }));
        }
        break;
        
      case 'answer':
        if (peerConnectionRef.current) {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
        break;
        
      case 'ice-candidate':
        if (peerConnectionRef.current && data.candidate) {
          try {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (e) {
            console.error('Error adding ICE candidate:', e);
          }
        }
        break;
        
      case 'call-accepted':
        setCallState('active');
        break;
        
      case 'call-rejected':
      case 'call-ended':
        endCall();
        break;
        
      default:
        break;
    }
  }, [callId]);

  // Initialize call
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const setupCall = async () => {
      // Get call data
      try {
        const response = await axios.get(`${API}/calls/${callId}`, { withCredentials: true });
        setCallData(response.data);
      } catch (error) {
        console.error('Error fetching call data:', error);
        // Call might be new, continue anyway
      }

      // Initialize media (audio only by default)
      const stream = await initializeMedia(false);
      if (!stream) {
        navigate(-1);
        return;
      }

      // Create peer connection
      createPeerConnection();

      // Connect to signaling WebSocket (using /api prefix for ingress routing)
      const ws = new WebSocket(`${WS_API}/ws/call/${callId}`);
      
      ws.onopen = async () => {
        console.log('Call WebSocket connected');
        
        if (isInitiator) {
          setCallState('ringing');
          // Create and send offer
          const offer = await peerConnectionRef.current.createOffer();
          await peerConnectionRef.current.setLocalDescription(offer);
          ws.send(JSON.stringify({
            type: 'offer',
            offer: offer,
            callId: callId,
            callerId: user?.user_id,
            callerName: user?.name
          }));
        } else {
          setCallState('ringing');
        }
      };
      
      ws.onmessage = handleSignalingMessage;
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      ws.onclose = () => {
        console.log('Call WebSocket closed');
      };
      
      wsRef.current = ws;
    };

    setupCall();

    return () => {
      endCall();
    };
  }, [callId, isAuthenticated, isInitiator, navigate, user, initializeMedia, createPeerConnection, handleSignalingMessage]);

  // End call function
  const endCall = useCallback(() => {
    // Stop call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    // Stop local media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'call-ended',
        callId: callId
      }));
      wsRef.current.close();
    }
    
    setCallState('ended');
    
    // Navigate back after a short delay
    setTimeout(() => {
      if (chatId) {
        navigate(`/chat/${chatId}`);
      } else {
        navigate(-1);
      }
    }, 1500);
  }, [callId, chatId, navigate]);

  // Toggle mute
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Toggle video
  const toggleVideo = async () => {
    if (isVideoOff) {
      // Enable video
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const videoTrack = videoStream.getVideoTracks()[0];
        
        // Add video track to local stream and peer connection
        localStreamRef.current.addTrack(videoTrack);
        
        if (peerConnectionRef.current) {
          peerConnectionRef.current.addTrack(videoTrack, localStreamRef.current);
        }
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
        
        setIsVideoOff(false);
      } catch (error) {
        console.error('Error enabling video:', error);
        toast.error('Could not access camera');
      }
    } else {
      // Disable video
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.stop();
        localStreamRef.current.removeTrack(videoTrack);
      }
      setIsVideoOff(true);
    }
  };

  // Toggle speaker
  const toggleSpeaker = () => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = !remoteVideoRef.current.muted;
      setIsSpeakerOff(remoteVideoRef.current.muted);
    }
  };

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="video-call-page">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => endCall()}
          className="text-white hover:bg-slate-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <p className="text-white font-medium">
            {callData?.other_party_name || (isInitiator ? 'Calling...' : 'Incoming Call')}
          </p>
          <p className="text-slate-400 text-sm">
            {callState === 'connecting' && 'Connecting...'}
            {callState === 'ringing' && (isInitiator ? 'Ringing...' : 'Incoming call')}
            {callState === 'active' && formatDuration(callDuration)}
            {callState === 'ended' && 'Call ended'}
          </p>
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Video area */}
      <div className="flex-1 relative flex items-center justify-center">
        {/* Remote video (full screen) */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
          style={{ display: callState === 'active' ? 'block' : 'none' }}
        />
        
        {/* Placeholder when no video */}
        {(callState !== 'active' || isVideoOff) && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
            <div className="w-32 h-32 bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-16 h-16 text-slate-400" />
            </div>
          </div>
        )}
        
        {/* Local video (picture-in-picture) */}
        <div className="absolute bottom-4 right-4 w-32 h-44 bg-slate-800 rounded-lg overflow-hidden border-2 border-slate-700">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ display: !isVideoOff ? 'block' : 'none' }}
          />
          {isVideoOff && (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-10 h-10 text-slate-400" />
            </div>
          )}
        </div>
        
        {/* Ringing animation */}
        {callState === 'ringing' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="w-32 h-32 bg-slate-700 rounded-full flex items-center justify-center animate-pulse">
                <Phone className="w-12 h-12 text-white" />
              </div>
              <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-emerald-500 animate-ping opacity-50" />
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-6 bg-slate-800/50 backdrop-blur">
        <div className="flex items-center justify-center gap-4">
          {/* Mute button */}
          <Button
            variant="outline"
            size="lg"
            onClick={toggleMute}
            className={`rounded-full w-14 h-14 ${
              isMuted 
                ? 'bg-rose-500 border-rose-500 text-white hover:bg-rose-600' 
                : 'border-slate-600 text-white hover:bg-slate-700'
            }`}
            data-testid="mute-btn"
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>
          
          {/* Video toggle button */}
          <Button
            variant="outline"
            size="lg"
            onClick={toggleVideo}
            className={`rounded-full w-14 h-14 ${
              isVideoOff 
                ? 'border-slate-600 text-white hover:bg-slate-700' 
                : 'bg-blue-500 border-blue-500 text-white hover:bg-blue-600'
            }`}
            data-testid="video-toggle-btn"
          >
            {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
          </Button>
          
          {/* End call button */}
          <Button
            variant="destructive"
            size="lg"
            onClick={endCall}
            className="rounded-full w-16 h-16 bg-rose-600 hover:bg-rose-700"
            data-testid="end-call-btn"
          >
            <PhoneOff className="w-7 h-7" />
          </Button>
          
          {/* Speaker toggle button */}
          <Button
            variant="outline"
            size="lg"
            onClick={toggleSpeaker}
            className={`rounded-full w-14 h-14 ${
              isSpeakerOff 
                ? 'bg-rose-500 border-rose-500 text-white hover:bg-rose-600' 
                : 'border-slate-600 text-white hover:bg-slate-700'
            }`}
            data-testid="speaker-btn"
          >
            {isSpeakerOff ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoCallPage;
